import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { TableLazyLoadEvent } from 'primeng/table';
import { firstValueFrom } from 'rxjs';
import { HasPermissionDirective } from '../../../../../../directives/has-permission.directive';
import { manualMeasureDefinition } from '../../../../../../entity-definitions/manual-measure.definition';
import { ManualMeasuresService } from '../../../../../../services/api/generated/manual-measures/manual-measures.service';
import { AssetMeasureTypeOptionsService } from '../../../../../../services/asset-measure-type-options.service';
import { UsedLocationOptionsService } from '../../../../../../services/used-location-options.service';
import { buildLocationFilterColumn } from '../../../../../../utils/location-filter';
import { ToastService } from '../../../../../../services/toast.service';
import { AssetStore } from '../../../../../../stores/asset.store';
import { ManualMeasureStore } from '../../../../../../stores/manual-measure.store';
import { ColumnConfig } from '../../../../../../types/entity-definition.interface';
import { DetailedManualMeasure } from '../../../../../../types/measure.interface';
import { MeasureType } from '../../../../../../types/table.interface';
import { notifyMeasureDeletionError } from '../../../../../../utils/measure-deletion-error.util';
import { PaginatedListHelper } from '../../../../../../utils/paginated-list.helper';
import { LazyLoadParams } from '../../../../../../types/lazy-load.interface';
import { RouteUtils } from '../../../../../../utils/route.utils';
import { PAGINATION_CONSTANTS } from '../../../../../../constants/pagination.constants';
import { buildBarcodeLabelCsv } from '../../../../../../utils/barcode-label-csv.utils';
import { buildBarcodeExportFilename } from '../../../../../../utils/export-filename.utils';
import { downloadBlob } from '../../../../../../utils/download.utils';
import { GenericParentGridComponent } from '../../../../../generic/generic-parent-grid.component';

@Component({
  selector: 'app-manual-measure-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [GenericParentGridComponent, HasPermissionDirective, TranslocoModule],
  templateUrl: './manual-measure-list.component.html',
})
export class ManualMeasureListComponent implements OnInit {
  private readonly assetStore = inject(AssetStore);
  private readonly measureTypeOptions = inject(AssetMeasureTypeOptionsService);
  private readonly usedLocations = inject(UsedLocationOptionsService);
  private readonly store = inject(ManualMeasureStore);
  private readonly measureApi = inject(ManualMeasuresService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslocoService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translations = toSignal(this.translateService.selectTranslation());

  readonly helper = new PaginatedListHelper<DetailedManualMeasure>(this.translateService, {
    globalFilterFields: ['name', 'measureTypeName', 'localisationName'],
    errorKey: 'common.errors.manualMeasureLoadFailed',
    stateKey: 'manual-measures-grid',
    loadAssetById: async id => {
      await this.assetStore.loadById(id);
    },
    isAssetLoaded: id => !!this.assetStore.byId()(id),
  });

  readonly entityDefinitionWithPermissions = computed(() => {
    this.translations();
    const columns: ColumnConfig<DetailedManualMeasure>[] = [
      {
        field: 'name',
        headerKey: 'common.labels.name',
        sortable: true,
        filterable: true,
        template: 'measureName',
        class: 'min-w-[200px]',
        contentClass: 'line-clamp-1',
        showTooltip: true,
      },
      {
        field: 'measureTypeName',
        filterField: 'measureTypeId',
        headerKey: 'common.labels.type',
        sortable: true,
        filterable: 'multiselect',
        options: this.measureTypeOptions.options(),
      },
      buildLocationFilterColumn<DetailedManualMeasure>(this.usedLocations.options('manual')(), 'server'),
      {
        field: 'priority',
        headerKey: 'common.labels.mandatory',
        sortable: true,
        filterable: 'checkbox',
        template: 'boolean',
      },
    ];
    const assetId = this.helper.state.currentAssetId() ?? undefined;
    return {
      ...manualMeasureDefinition,
      defaultSortField: null,
      list: { columns },
      actionButtons: {
        ...manualMeasureDefinition.actionButtons,
        parent: {
          ...manualMeasureDefinition.actionButtons?.parent,
          addPermission: { method: 'postManualMeasureList' as const, assetId },
          editPermission: { method: 'putMeasureDetailResource' as const, assetId },
          deletePermission: { method: 'deleteMeasureDetailResource' as const, assetId },
        },
      },
      rowLinkHref: (measure: DetailedManualMeasure) => RouteUtils.getMeasurePath(measure.assetId, 'manual', measure.id),
    };
  });

  readonly tablePermissionConfig = computed(() => ({
    method: 'getMeasureDetailResource' as const,
    assetId: this.helper.state.currentAssetId() ?? undefined,
  }));

  readonly gridStateKey = computed(() => this.helper.buildStateKey('manual-measures-grid'));

  // Read by the Mesures page to grey its kebab entry while a sheet is being assembled.
  readonly isExporting = signal(false);

  readonly noAssetSelectedKey = (): string => 'common.messages.noAssetSelected.measures';

  constructor() {
    effect(() => {
      const assetId = this.helper.state.currentAssetId();
      this.measureTypeOptions.load(assetId);
      this.usedLocations.load(assetId, 'manual');
    });
  }

  ngOnInit(): void {
    this.helper.setupLangChangeSubscription(this.destroyRef, () =>
      this.loadData(this.helper.lastParams ?? this.helper.createInitialParams()),
    );
    this.helper.setupRouteSubscription(this.route, this.destroyRef, async shouldLoad => {
      if (shouldLoad) {
        await this.loadData(this.helper.createInitialParams());
      }
    });
  }

  async onLazyLoad(event: TableLazyLoadEvent): Promise<void> {
    await this.helper.handleLazyLoad(event, params => this.loadData(params));
  }

  private async loadData(params: LazyLoadParams): Promise<void> {
    const assetId = this.helper.state.currentAssetId();
    if (!assetId) return;

    await this.helper.executeLoad(params, async () => {
      const response = await firstValueFrom(
        this.measureApi.getDetailedMeasuresResource({
          assetId,
          type: MeasureType.MANUAL,
          page: params.pagination.page,
          pageSize: params.pagination.pageSize,
          ...(params.sorts?.length ? { sorts: JSON.stringify(params.sorts) } : {}),
          ...(params.filters?.length ? { filters: JSON.stringify(params.filters) } : {}),
        }),
      );
      return {
        data: response.measures as unknown as DetailedManualMeasure[],
        total: response.total,
        page: response.page,
        pageSize: response.limit,
      };
    });
  }

  createMeasure(): void {
    const assetId = this.helper.state.currentAssetId();
    if (!assetId) return;
    void this.router.navigate(RouteUtils.getMeasurePath(assetId, 'manual', 'new'));
  }

  /** Barcode label sheet, narrowed by whatever the grid currently filters and sorts on.
   *  Lives here rather than on the page that triggers it: this component owns that state. */
  async exportBarcodes(): Promise<void> {
    const assetId = this.helper.state.currentAssetId();
    if (!assetId || this.isExporting()) return;

    this.isExporting.set(true);
    try {
      const rows = await this.fetchAllForExport(assetId);
      if (rows.length === 0) {
        this.toastService.warning('barcodeExport.empty');
        return;
      }

      const assetName = this.assetStore.byId()(assetId)?.name;
      const csv = buildBarcodeLabelCsv(rows, assetName ?? '', {
        barcode: this.translateService.translate('barcodeExport.columns.barcode'),
        name: this.translateService.translate('barcodeExport.columns.name'),
        measureType: this.translateService.translate('barcodeExport.columns.measureType'),
        asset: this.translateService.translate('barcodeExport.columns.asset'),
      });
      // Without the BOM, Excel reads the UTF-8 accents as mojibake.
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
      downloadBlob(blob, buildBarcodeExportFilename(assetName, new Date()));
    } catch (error) {
      this.toastService.error('barcodeExport.error');
      console.error(this.translateService.translate('barcodeExport.error'), error);
    } finally {
      this.isExporting.set(false);
    }
  }

  // The server clamps pageSize to 150, so a single oversized fetch would truncate in
  // silence. Both of its ordering branches end on measure id, so paging is stable.
  private async fetchAllForExport(assetId: number): Promise<DetailedManualMeasure[]> {
    const params = this.helper.lastParams ?? this.helper.createInitialParams();
    const rows: DetailedManualMeasure[] = [];
    let page = 1;
    let total = 0;

    do {
      const response = await firstValueFrom(
        this.measureApi.getDetailedMeasuresResource({
          assetId,
          type: MeasureType.MANUAL,
          page,
          pageSize: PAGINATION_CONSTANTS.MAX_PAGE_SIZE,
          ...(params.sorts?.length ? { sorts: JSON.stringify(params.sorts) } : {}),
          ...(params.filters?.length ? { filters: JSON.stringify(params.filters) } : {}),
        }),
      );
      const batch = response.measures as unknown as DetailedManualMeasure[];
      if (batch.length === 0) break;
      rows.push(...batch);
      total = response.total;
      page += 1;
    } while (rows.length < total);

    return rows;
  }

  deleteMeasure(measure: DetailedManualMeasure): void {
    if (!measure?.id) {
      console.error(this.translateService.translate('common.errors.invalidMeasureDeletion'));
      return;
    }

    this.store
      .delete(measure.id)
      .then(() => this.helper.reloadWithLastParams(params => this.loadData(params)))
      .catch(error => {
        notifyMeasureDeletionError(error, this.toastService);
        console.error(this.translateService.translate('common.errors.manualMeasureDeleteFailed'), error);
      });
  }
}
